import styled, { css, keyframes } from 'styled-components';

import { ANIM, COLORS, DAY_BARS, HOME } from '../../theme/tokens';
import { Text } from '../Text';

// The legend's "live" dot breathes — a gentle opacity + scale pulse so the Today
// encoding reads as still updating.
const pulse = keyframes`
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.45;
    transform: scale(0.82);
  }
`;

const BAR_H = 13;
// Shared geometry for the prior-day TICK so it reads identically on the bar, in the
// legend, and in the tooltip (the one consistent "yesterday" mark).
const TICK_W = 2;
const TICK_H = 11;

/** Full-width host, sized to the same `height` the LineChart it replaces reserves.
 *  `position: relative` anchors the floating hover tooltip. */
export const Root = styled.div<{ $h: number }>`
  position: relative;
  width: 100%;
  min-width: 0;
  height: ${(p) => p.$h}px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 12px;
`;

/** Owns the shared column tracks so EVERY row's bar starts (and ends) at the same x,
 *  regardless of label length — the name column sizes to the longest name in the set. */
export const Rows = styled.div`
  display: grid;
  grid-template-columns: minmax(64px, max-content) 1fr auto;
  column-gap: 12px;
  row-gap: 11px;
  align-items: center;
`;

/** One series: name · bullet track · the current-vs-prior value pair. Adopts the
 *  parent's column tracks (subgrid) so all rows line up; hovering anywhere on the
 *  row raises the tooltip. */
export const Row = styled.div`
  display: grid;
  grid-template-columns: subgrid;
  grid-column: 1 / -1;
  align-items: center;
  cursor: default;
`;

export const Name = styled(Text)`
  font-size: 12.5px;
  font-weight: 600;
  color: ${HOME.t300};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** The bullet track — an unfilled pill the current-day fill grows across and the
 *  prior-day marker sits on. `$compact` thins it for the channel mini-cards. */
export const Track = styled.div<{ $compact?: boolean }>`
  position: relative;
  height: ${(p) => (p.$compact ? 10 : BAR_H)}px;
  border-radius: 7px;
  background: ${DAY_BARS.track};
`;

/** Compact layout (channel mini-cards): bars only — no name column, value pairs, or
 *  legend (the card carries the figures + the grid's shared series legend). */
export const CompactRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const CompactRow = styled.div`
  width: 100%;
  cursor: default;
`;

/** The current day's value as a pill fill. Solid for normal series; a dashed
 *  outline for a `dashed` series, mirroring the LineChart legend. */
export const Fill = styled.div<{ $pct: number; $color: string; $grown: boolean; $outline: boolean }>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: ${(p) => (p.$grown ? p.$pct : 0)}%;
  min-width: ${(p) => (p.$grown && p.$pct > 0 ? '4px' : '0')};
  border-radius: 7px;
  overflow: hidden;
  transition: width ${ANIM.DAY_BARS_GROW_MS}ms ${ANIM.EASE};
  ${(p) =>
    p.$outline
      ? css`
          background: transparent;
          border: 1.5px dashed ${p.$color};
        `
      : css`
          background: ${p.$color};
        `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/** The prior day, marked as a thin reference tick at its value on the same track —
 *  the pace line the current fill is measured against. The SAME tick is used in the
 *  legend and tooltip, so "yesterday" reads identically everywhere. */
export const Marker = styled.div<{ $pct: number; $grown: boolean }>`
  position: absolute;
  top: -2px;
  bottom: -2px;
  /* Clamp so the tick always sits a few px INSIDE the track — at a zero value it
   *  rests just inside the rounded left edge, never straddling or poking outside. */
  left: clamp(5px, ${(p) => (p.$grown ? p.$pct : 0)}%, calc(100% - 5px));
  width: ${TICK_W}px;
  margin-left: ${-TICK_W / 2}px;
  border-radius: 2px;
  background: ${COLORS.textStrong};
  opacity: 0.5;
  /* Decorative reference tick — never intercept the row's hover hit-test. */
  pointer-events: none;
  transition: left ${ANIM.DAY_BARS_GROW_MS}ms ${ANIM.EASE};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/** The current · prior value pair at the row's end. */
export const Vals = styled.div`
  font-size: 12.5px;
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  color: ${HOME.t500};
`;

export const Cur = styled.b`
  color: ${COLORS.textStrong};
  font-weight: 700;
`;

/* ── Shared swatches (used by BOTH the legend and the tooltip, so the two stay in
   lockstep): a filled bar = current day, a tick = prior day. ─────────────────── */

/** Fixed-footprint swatch cell so the bar + tick line up and labels stay aligned. */
export const Glyph = styled.span`
  width: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
`;

/** Current-day swatch — a small filled bar. */
export const SwatchBar = styled.i<{ $color: string }>`
  width: 14px;
  height: 8px;
  border-radius: 3px;
  background: ${(p) => p.$color};
`;

/** Prior-day swatch — the same reference tick the bar uses. */
export const SwatchTick = styled.i`
  width: ${TICK_W}px;
  height: ${TICK_H}px;
  border-radius: 2px;
  background: ${COLORS.textStrong};
  opacity: 0.5;
`;

/** Key for the two encodings (filled bar = today, tick = yesterday). */
export const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 2px;
`;

export const LegItem = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`;

export const LegText = styled(Text)`
  font-size: 11px;
  color: ${HOME.t500};
`;

/** The pulsing "live" dot beside the Today key — marks the current day as live. */
export const LiveDot = styled.i`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex: none;
  background: ${HOME.success};
  box-shadow: 0 0 0 3px ${HOME.successBg};
  animation: ${pulse} ${ANIM.DAY_BARS_PULSE_MS}ms ${ANIM.EASE} infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/**
 * Floating hover readout — a dark card naming the hovered series and listing its
 * current-day vs prior-day values, with the SAME bar/tick swatches as the legend.
 * Positioned at the pointer (flipping left near the right edge); never intercepts it.
 */
export const Tooltip = styled.div<{ $on: boolean; $flip: boolean }>`
  position: absolute;
  z-index: 5;
  pointer-events: none;
  min-width: 140px;
  padding: 9px 11px;
  border-radius: 11px;
  background: ${HOME.menuBg};
  border: 1px solid ${COLORS.panelBorder};
  box-shadow: ${HOME.menuShadow};
  backdrop-filter: blur(6px);
  opacity: ${(p) => (p.$on ? 1 : 0)};
  transform: translate(${(p) => (p.$flip ? 'calc(-100% - 10px)' : '10px')}, calc(-100% - 10px))
    translateY(${(p) => (p.$on ? '0' : `${ANIM.CHART_TIP_RISE_PX}px`)});
  transition:
    opacity ${(p) => (p.$on ? ANIM.CHART_TIP_ENTER_MS : ANIM.CHART_TIP_EXIT_MS)}ms ${ANIM.EASE},
    transform ${(p) => (p.$on ? ANIM.CHART_TIP_ENTER_MS : ANIM.CHART_TIP_EXIT_MS)}ms ${ANIM.EASE};
  will-change: opacity, transform;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const TipHead = styled(Text)`
  display: block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: ${HOME.t400};
  margin-bottom: 7px;
  white-space: nowrap;
`;

export const TipRows = styled.div`
  display: grid;
  gap: 6px;
`;

export const TipRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 10px;
`;

export const TipName = styled(Text)`
  font-size: 12px;
  font-weight: 400;
  color: ${HOME.t300};
  white-space: nowrap;
`;

export const TipValue = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.textStrong};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;
